import type {
  TeamBudgetCheckoutStatus,
  TeamBudgetPaymentChoice,
  TeamBudgetSessionFields,
} from '../types'

/**
 * SN-8441 / SN-8442 — the payment step's view of the rep's choice as the SERVER
 * holds it. A chosen payment is taken before the team is registered: the step only
 * offers the card form once the session is priced for exactly the choice on screen.
 */

/** A payment was taken (or may have been) for the session: never offer paying again. */
const FUNDED_STATUSES: ReadonlySet<TeamBudgetCheckoutStatus> = new Set([
  'submitting',
  'unknown',
  'captured',
  'settled',
  'parked',
  'needs_review',
])

/**
 * Whether the api2 behind this session takes a Team Budget payment before
 * registering (it always answers `team_budget_checkout`, null or not). An older one
 * never does: the step then offers only Not now — never the old confirm-first path.
 */
export const supportsTeamBudgetCheckout = (
  session?: TeamBudgetSessionFields | null
): boolean =>
  Boolean(session) &&
  Object.prototype.hasOwnProperty.call(session, 'team_budget_checkout')

/** The server recorded a choice (Not now included) for the session. */
export const hasRecordedChoice = (
  session?: TeamBudgetSessionFields | null
): boolean => Boolean(session?.team_budget_choice_at)

/**
 * The choice the payment step is entered with — what step 3 sends on its way there:
 * the rep's recorded choice when there is one, else the deposit; Not now (an
 * explicit null) when paying isn't offered. Undefined: send nothing (not a Team
 * Budget rep, or an api2 without the checkout).
 */
export const teamBudgetEntryChoiceCents = ({
  isTeamBudgetRep,
  offersPayment,
  depositCents,
  session,
}: {
  isTeamBudgetRep: boolean
  offersPayment: boolean
  depositCents: number | null
  session?: TeamBudgetSessionFields | null
}): number | null | undefined => {
  if (!isTeamBudgetRep || !supportsTeamBudgetCheckout(session)) {
    return undefined
  }

  if (!offersPayment || depositCents === null) {
    return null
  }

  if (hasRecordedChoice(session)) {
    return session?.team_budget_payment_cents ?? null
  }

  return depositCents
}

/** The choice on screen for what the server recorded (the deposit when nothing is). */
export const choiceFromSession = (
  session: TeamBudgetSessionFields | null | undefined,
  depositCents: number | null
): { choice: TeamBudgetPaymentChoice; customCents: number | null } => {
  if (!hasRecordedChoice(session)) {
    return { choice: 'deposit', customCents: null }
  }

  const cents = session?.team_budget_payment_cents ?? null

  if (cents === null) {
    return { choice: 'later', customCents: null }
  }

  return cents === depositCents
    ? { choice: 'deposit', customCents: null }
    : { choice: 'custom', customCents: cents }
}

/**
 * The cents a choice sends: the deposit, the custom amount, or null for Not now.
 * Undefined while "a different amount" has none entered yet.
 */
export const centsForChoice = ({
  choice,
  customCents,
  depositCents,
}: {
  choice: TeamBudgetPaymentChoice
  customCents: number | null
  depositCents: number | null
}): number | null | undefined => {
  if (choice === 'later') {
    return null
  }

  if (choice === 'deposit') {
    return depositCents ?? undefined
  }

  return customCents ?? undefined
}

/**
 * The session is priced for exactly this choice: the server recorded it and — for a
 * payment — its checkout is open, so the card form may take it.
 */
export const isPricedFor = (
  session: TeamBudgetSessionFields | null | undefined,
  cents: number | null | undefined
): boolean => {
  if (cents === undefined || !hasRecordedChoice(session)) {
    return false
  }

  if ((session?.team_budget_payment_cents ?? null) !== cents) {
    return false
  }

  return cents === null || session?.team_budget_checkout?.status === 'open'
}

/** A payment was taken (or may have been): the step never offers to pay again. */
export const isCheckoutFunded = (
  session?: TeamBudgetSessionFields | null
): boolean => {
  const status = session?.team_budget_checkout?.status

  return status !== undefined && FUNDED_STATUSES.has(status)
}

/**
 * F1 — the session PROVES the rep paid (or was invoiced) at registration: the server
 * says so, or the owner's summary is on it. The confirmation step keeps describing
 * that payment after Team Budget is switched off on the division.
 */
export const hasTeamBudgetPaymentHistory = (
  session?: TeamBudgetSessionFields | null
): boolean =>
  Boolean(session?.has_team_budget_payment) ||
  Boolean(session?.linked_team_registration?.rep_payment)
